import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import useSwipeToClose from '../../hooks/useSwipeToClose';
import Spinner from '../UI/Spinner';

function RemoveAllConfirmationDrawer({ 
  isOpen, 
  onClose, 
  mealType, 
  onConfirmRemove,
  isLoading = false
}) {
  const { t } = useTranslation(['mealplan', 'common']);
  const { containerRef, dragOffset, shouldRender, isVisible, DragHandle } = useSwipeToClose(onClose, isOpen);

  if (!shouldRender || !mealType) return null;

  return createPortal(
    <div 
      className={`fixed flex items-end justify-center inset-0 z-[200] drawer-overlay`}
      style={{
        backgroundColor: `rgba(13, 12, 7, ${isVisible ? 0.5 : 0})`,
        transition: 'background-color 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      }}
      onClick={isLoading ? undefined : onClose}
    >
      <div 
        ref={containerRef}
        className="flex flex-col w-full bg-[#F8F8F7] rounded-[12px_12px_0px_0px] p-8 pb-[116px] max-w-5xl mx-auto shadow-lg drawer-content overflow-hidden"
        style={{
          height: 'auto',
          transform: isVisible ? `translateY(${Math.max(0, dragOffset)}px)` : 'translateY(100%)',
          transition: dragOffset === 0 ? 'transform 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <DragHandle />
        {/* Close button */}
        <button 
          onClick={onClose}
          disabled={isLoading}
          aria-label={t('common:buttons.close', 'Close')}
          className={`absolute top-1 right-1 flex justify-center items-center w-[36px] h-[36px] rounded-[12px] transition-colors ${isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200'}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M16.381 6.38079C16.7227 6.03908 17.2776 6.03908 17.6193 6.38079C17.961 6.7225 17.961 7.27737 17.6193 7.61907L13.2385 11.9999L17.6193 16.3808L17.6789 16.4472C17.9595 16.7909 17.9398 17.2986 17.6193 17.6191C17.2989 17.9395 16.7911 17.9592 16.4474 17.6786L16.381 17.6191L12.0002 13.2382L7.61932 17.6191C7.27761 17.9608 6.72275 17.9608 6.38104 17.6191C6.03933 17.2774 6.03933 16.7225 6.38104 16.3808L10.7619 11.9999L6.38104 7.61907L6.32147 7.55267C6.04089 7.20899 6.06059 6.70124 6.38104 6.38079C6.70149 6.06034 7.20923 6.04064 7.55291 6.32122L7.61932 6.38079L12.0002 10.7617L16.381 6.38079Z" fill="#56554D"/>
          </svg>
        </button>
        <div className="pt-2 w-full">
          <h2 className="
                    text-[var(--Color-Grays-Gray-10,#0D0C07)]
                    text-[16px]
                    font-bold
                    leading-[32px]
                    w-full
                    mb-4
                  ">{t('confirmations.removeAllDrawer.title', {
                    mealType: mealType.toLowerCase(),
                    defaultValue: 'Remove all {{mealType}} meals?'
                  })}</h2>
          <p className="
                    text-[var(--Color-Grays-Gray-7,#65635D)]
                    text-[14px]
                    font-normal
                    leading-[24px]
                    mb-8
                  ">{t('confirmations.removeAllDrawer.message', 'They will leave your plan, but you can create new meals later.')}</p>
          
          <div
              className="
                fixed bottom-0 left-0 right-0
                pt-2 pb-4 px-0
                flex justify-center
                bg-white
                z-10
                rounded-[12px_12px_0px_0px]
                shadow-[0px_0px_1px_0px_rgba(13,12,7,0.12),_0px_-4px_24px_0px_rgba(13,12,7,0.12),_1px_-2px_8px_0px_rgba(13,12,7,0.08)]
              "
            >
                <div className="w-full max-w-5xl px-6 gap-4 flex items-center justify-between">
                <button
                  className={`
                        inline-flex justify-center items-center gap-1
                        h-[56px] px-[24px] py-[14px]
                        rounded-[12px]
                        border-[0.5px] border-[#E2E1DF]
                        bg-[#E2E1DF]
                        text-[#0D0C07] text-center
                        text-[15.2px] font-medium leading-[28px]
                        transition-colors
                        ${isLoading ? 'opacity-50 cursor-not-allowed' : 'md:hover:bg-[#D5D4D1]'}
                        w-full
                      `}
                  onClick={onClose}
                  disabled={isLoading}
                >
                  <span>{t('common:buttons.cancel', 'Cancel')}</span>
                </button>
                <button
                  className={`
                        inline-flex justify-center items-center gap-1
                        h-[56px] px-[24px] py-[14px]
                        rounded-[12px]
                        border-[0.5px] border-[#D40B0B]
                        bg-[#D40B0B]
                        text-[#fff] text-center
                        text-[15.2px] font-medium leading-[28px]
                        transition-colors
                        ${isLoading ? 'opacity-70 cursor-not-allowed' : 'md:hover:bg-[#B80A0A]'}
                        w-full
                      `}
                  onClick={onConfirmRemove}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Spinner size="sm" color="#fff" />
                      <span className="ml-2">{t('common:buttons.removing', 'Removing...')}</span>
                    </>
                  ) : (
                    <span>{t('common:buttons.remove', 'Remove')}</span>
                  )}
                </button>
                </div>
            </div>
        </div>
      </div>
    </div>, document.body
  );
}

export default RemoveAllConfirmationDrawer;
